import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Sparkles, FileText, AlertTriangle, Calendar, ListTodo, Play, Clock, CheckCircle, XCircle, Loader2, Settings, Zap } from 'lucide-react';
import AgentSuggestionCard from './AgentSuggestionCard';
import AgentSettingsModal from './AgentSettingsModal';
import AgentOnboardingModal from './AgentOnboardingModal';
import AgentRunHistory from './AgentRunHistory';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGENT_TYPES = {
  DOCUMENT: {
    name: 'DokumentAgent',
    icon: FileText,
    color: 'blue',
    description: 'Analyserer dokumenter og identifiserer mangler'
  },
  RISK: {
    name: 'RisikoAgent',
    icon: AlertTriangle,
    color: 'orange',
    description: 'Identifiserer juridiske risikoer'
  },
  TIMELINE: {
    name: 'TidslinjeAgent',
    icon: Calendar,
    color: 'purple',
    description: 'Foreslår hendelser for tidslinje'
  },
  TASK: {
    name: 'OppgaveAgent',
    icon: ListTodo,
    color: 'green',
    description: 'Foreslår neste oppgaver'
  }
};

const AgentDashboard = ({ caseId }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningAgent, setRunningAgent] = useState(null);
  const [settings, setSettings] = useState(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    // Check if user has seen onboarding
    const hasSeenOnboarding = localStorage.getItem('lexy_agent_onboarding_seen');
    if (!hasSeenOnboarding) {
      setShowOnboarding(true);
    }
    
    fetchData();
  }, [caseId]);

  const fetchData = async () => {
    try {
      const [suggestionsRes, runsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/agents/suggestions/${caseId}?status=PENDING`),
        axios.get(`${API}/agents/runs/${caseId}?limit=5`),
        axios.get(`${API}/agents/settings/${caseId}`).catch(() => ({ data: null }))
      ]);
      
      setSuggestions(suggestionsRes.data);
      setRuns(runsRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Error fetching agent data:', error);
      toast.error('Kunne ikke laste agent-data');
    } finally {
      setLoading(false);
    }
  };

  const runAgent = async (agentType) => {
    setRunningAgent(agentType);
    try {
      const response = await axios.post(`${API}/agents/run`, {
        case_id: caseId,
        agent_type: agentType,
        triggered_by: 'USER'
      });
      
      toast.success(`${AGENT_TYPES[agentType].name} fullført! Genererte ${response.data.suggestion_count} forslag.`);
      await fetchData();
    } catch (error) {
      console.error('Error running agent:', error);
      toast.error(error.response?.data?.detail || 'Kunne ikke kjøre agent');
    } finally {
      setRunningAgent(null);
    }
  };

  const handleSuggestionUpdate = async () => {
    await fetchData();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const enabledAgents = settings?.enabled_agents || {};

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      
      {/* Header - Dark AI section */}
      <div className="bg-gray-900 rounded-xl p-6 shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center shadow-lg shadow-violet-500/20">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              AI Agenter
              <span className="px-2 py-0.5 bg-violet-500/20 text-violet-300 text-xs font-medium rounded-full border border-violet-500/30">
                AI-drevet
              </span>
            </h2>
            <p className="text-sm text-gray-400 mt-2 ml-13">
              Proaktive AI-assistenter som hjelper deg med saksarbeidet
            </p>
          </div>
          <Button
            onClick={() => setShowSettingsModal(true)}
            variant="outline"
            className="flex items-center gap-2 bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700 hover:text-white"
          >
            <Settings className="w-4 h-4" />
            Innstillinger
          </Button>
        </div>
      </div>

      {/* Onboarding Modal */}
      {showOnboarding && (
        <AgentOnboardingModal
          onClose={() => setShowOnboarding(false)}
        />
      )}

      {/* Settings Modal */}
      {showSettingsModal && (
        <AgentSettingsModal
          caseId={caseId}
          settings={settings}
          onClose={() => setShowSettingsModal(false)}
          onSave={fetchData}
        />
      )}

      {/* Agent Status Cards - Light base with purple AI accents */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Object.entries(AGENT_TYPES).map(([type, agent]) => {
          const Icon = agent.icon;
          const lastRun = runs.find(r => r.agent_type === type);
          const isEnabled = enabledAgents[type] !== false;
          const isRunning = runningAgent === type;
          const autoTriggerEnabled = settings?.auto_trigger_rules?.[type]?.enabled || false;
          
          return (
            <div
              key={type}
              className={`bg-white border rounded-xl p-5 transition-all shadow-sm ${
                isEnabled ? 'border-gray-200' : 'border-gray-100 opacity-60'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="p-2 rounded-lg bg-gray-100">
                  <Icon className="w-5 h-5 text-gray-600" />
                </div>
                <div className="flex flex-col gap-1 items-end">
                  {isEnabled ? (
                    <span className="px-2 py-1 text-xs font-medium bg-emerald-50 text-emerald-700 rounded-full">
                      Aktiv
                    </span>
                  ) : (
                    <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                      Pause
                    </span>
                  )}
                  {autoTriggerEnabled && (
                    <span className="px-2 py-1 text-xs font-medium bg-violet-50 text-violet-700 rounded-full flex items-center gap-1">
                      <Zap className="w-3 h-3" />
                      Auto
                    </span>
                  )}
                </div>
              </div>
              
              <h3 className="font-semibold text-gray-900 text-sm mb-1">{agent.name}</h3>
              <p className="text-xs text-gray-600 mb-3">{agent.description}</p>
              
              {lastRun && (
                <div className="text-xs text-gray-500 mb-3 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Sist: {new Date(lastRun.created_at).toLocaleDateString('nb-NO')}
                </div>
              )}
              
              {/* AI action button gets purple accent */}
              <Button
                size="sm"
                onClick={() => runAgent(type)}
                disabled={!isEnabled || isRunning}
                className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white"
              >
                {isRunning ? (
                  <>
                    <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                    Kjører...
                  </>
                ) : (
                  <>
                    <Play className="w-3 h-3 mr-2" />
                    Kjør agent
                  </>
                )}
              </Button>
            </div>
          );
        })}
      </div>

      {/* Pending Suggestions */}
      {suggestions.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              Ventende forslag ({suggestions.length})
            </h3>
          </div>
          
          <div className="space-y-3">
            {suggestions.map(suggestion => (
              <AgentSuggestionCard
                key={suggestion.id}
                suggestion={suggestion}
                onUpdate={handleSuggestionUpdate}
              />
            ))}
          </div>
        </div>
      )}

      {suggestions.length === 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center shadow-sm">
          <div className="w-12 h-12 bg-gradient-to-br from-violet-100 to-purple-100 rounded-lg flex items-center justify-center mx-auto mb-3">
            <Sparkles className="w-6 h-6 text-violet-600" />
          </div>
          <h3 className="text-sm font-medium text-gray-900 mb-1">Ingen ventende forslag</h3>
          <p className="text-sm text-gray-600">
            Kjør en agent for å få AI-drevne forslag til forbedringer
          </p>
        </div>
      )}

      {/* Agent Run History - Expandable */}
      {runs.length > 0 && (
        <AgentRunHistory runs={runs} onRefresh={fetchData} />
      )}
    </div>
  );
};

export default AgentDashboard;
